import React from 'react'
import PropTypes from 'prop-types'
import { Box, ButtonBase, Stack, Typography } from '@mui/material'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { useSmartTranslation } from '../../../../../../../generic/hooks/useSmartTranslation'
import { useDateFormatter } from '../../../../../../../generic/hooks/useDateFormatter'
import { JOB_DETAIL_TAB } from '../../../utils/jobDetailTabs'
import { EMPTY_VALUE, activeDeviceCount, formatSince, joinOrEmpty, phaseKey, workerFullName } from '../../../utils/jobDetailFormat'
import { coveragePercent } from '../../../utils/jobDetailInspections'
import { DetailSection, EllipsisText, HeaderCell, OVERLINE_SX, Pill } from '../JobDetailPrimitives'
import { SystemValueChip } from '../../../../../../../generic/systemValues/SystemValueChip'

// Historia 160 (CU1/CU3, RN11, RN15-RN19, RN25, RN28, mock 1) — pestaña General:
//   · CANTIDADES — cinco cifras, SIN "En curso" (D19);
//   · MÁQUINA Y OPERARIO — una fila por máquina en el orden de `devices`, marca "Planificada" según
//     `devices[].planned` (G7), grupos y operarios con elipsis + tooltip (D23/D-T3); más de 3 filas se
//     desplazan DENTRO de la tabla (RN47); cada fila activa lleva el chip de su fase junto al estado de
//     planta y, sin filas activas, se lee "Sin ejecución activa" bajo la tabla (E-9/RN57);
//   · RESUMEN DE LOS DEMÁS BLOQUES — una tarjeta por pestaña visible (sin Documentos), que la abre.

const DEVICE_STATUS_TONE = {
  WORKING: 'info',
  IDLE: 'default',
  STOPPED: 'error',
  ERROR: 'error',
  OFF: 'default',
  NON_OPERATIVE: 'default'
}

// Tres filas caben a 1920×1080 (RN47); la cuarta ya desplaza dentro de la tabla.
const MACHINE_ROW_HEIGHT = 52
const MACHINE_ROWS_VISIBLE = 3
const MACHINE_GRID = 'minmax(0, 1.2fr) minmax(0, 1fr) minmax(0, 0.8fr) minmax(0, 1.6fr) minmax(0, 0.7fr)'

const Quantity = ({ label, value, color = 'text.primary', testId }) => (
  <Box sx={{ px: 3, py: 1.5, borderRight: 1, borderColor: 'divider', minWidth: 150 }} data-testid={testId}>
    <Typography sx={OVERLINE_SX}>{label}</Typography>
    <Typography sx={{ fontSize: '1.9rem', fontWeight: 800, color, lineHeight: 1.2 }}>{value}</Typography>
  </Box>
)

Quantity.propTypes = { label: PropTypes.string, value: PropTypes.number, color: PropTypes.string, testId: PropTypes.string }

const SummaryCard = ({ title, value, detail, valueColor = 'text.primary', onClick, testId }) => (
  <ButtonBase
    onClick={onClick}
    data-testid={testId}
    sx={{ flex: '1 1 0', minWidth: 0, display: 'block', textAlign: 'left', p: 2, borderRadius: 2, border: 1, borderColor: 'divider', bgcolor: 'background.row2' }}
  >
    <Stack direction='row' justifyContent='space-between' alignItems='center'>
      <Typography sx={OVERLINE_SX}>{title}</Typography>
      <ChevronRightIcon fontSize='small' sx={{ color: 'text.secondary' }} />
    </Stack>
    <Typography sx={{ fontSize: '1.7rem', fontWeight: 800, color: valueColor, lineHeight: 1.3 }} data-testid={testId + '-value'}>{value}</Typography>
    <Typography variant='body2' color='text.secondary' data-testid={testId + '-detail'}>{detail}</Typography>
  </ButtonBase>
)

SummaryCard.propTypes = {
  title: PropTypes.string,
  value: PropTypes.string,
  detail: PropTypes.string,
  valueColor: PropTypes.string,
  onClick: PropTypes.func,
  testId: PropTypes.string
}

const materialsDetail = (pending, partComponents, t_) => {
  if (pending > 0) return t_('{{count}} pending', { count: pending })
  if (partComponents > 0) return t_('{{count}} without consumption record', { count: partComponents })
  return t_('All complete')
}

const GeneralTab = ({ detail = {}, visibleTabs = [], onOpenTab = () => {} }) => {
  const { t_ } = useSmartTranslation()
  const formatter = useDateFormatter()
  const quantities = detail.quantities || {}
  const devices = detail.devices || []
  const summary = detail.summary || {}
  const materials = summary.materials || {}
  const inspections = summary.inspections || {}
  const incidences = summary.incidences || {}
  // E-7 (RN55): un componente pieza no es ni completo ni pendiente (MIC no registra su consumo).
  const partComponents = materials.partComponents || 0
  const pendingMaterials = (materials.total || 0) - partComponents - (materials.complete || 0)
  const isVisible = (tab) => visibleTabs.includes(tab)

  return (
    <Stack spacing={2}>
      <DetailSection title={t_('Quantities')} testId='general-quantities'>
        <Stack direction='row' sx={{ py: 1 }}>
          <Quantity label={t_('Order quantity')} value={quantities.orderQuantity || 0} testId='quantity-order' />
          <Quantity label={t_('Available parts')} value={quantities.available || 0} color='info.light' testId='quantity-available' />
          <Quantity label={t_('Finished parts')} value={quantities.finished || 0} color='primary.main' testId='quantity-finished' />
          <Quantity label={t_('Non-conforming parts')} value={quantities.nonConforming || 0} color='error.light' testId='quantity-non-conforming' />
          <Quantity label={t_('Pending parts')} value={quantities.pending || 0} testId='quantity-pending' />
        </Stack>
      </DetailSection>

      <DetailSection title={t_('Machine and operator')} testId='general-machines'>
        <Box role='table' aria-label={t_('Machine and operator')}>
          <Box role='row' sx={{ display: 'grid', gridTemplateColumns: MACHINE_GRID, gap: 2, px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
            <HeaderCell>{t_('Machine')}</HeaderCell>
            <HeaderCell>{t_('Machine group')}</HeaderCell>
            <HeaderCell>{t_('Status')}</HeaderCell>
            <HeaderCell>{t_('Worker')}</HeaderCell>
            <HeaderCell align='right'>{t_('Since')}</HeaderCell>
          </Box>
          {devices.length === 0
            ? <Typography sx={{ px: 2, py: 1.5 }} color='text.secondary' data-testid='machines-not-planned'>{t_('Not planned')}</Typography>
            : (
              <Box data-testid='machine-rows' sx={{ maxHeight: MACHINE_ROW_HEIGHT * MACHINE_ROWS_VISIBLE, overflowY: 'auto' }}>
                {devices.map((device) => {
                  const since = formatSince(device.since, formatter)
                  const known = device.deviceStatus && device.deviceStatus !== 'UNKNOWN'
                  return (
                    <Box
                      role='row'
                      key={device.id}
                      data-testid={'machine-row-' + device.id}
                      sx={{ display: 'grid', gridTemplateColumns: MACHINE_GRID, gap: 2, alignItems: 'center', px: 2, minHeight: MACHINE_ROW_HEIGHT, borderBottom: 1, borderColor: 'divider', '&:last-of-type': { borderBottom: 0 } }}
                    >
                      <Stack role='cell' direction='row' alignItems='center' spacing={1} sx={{ minWidth: 0 }}>
                        <EllipsisText text={device.name || ''} sx={{ fontWeight: 800, fontSize: '1.05rem' }} />
                        {device.planned === true ? <Pill label={t_('Planned machine')} tone='success' testId={'machine-planned-' + device.id} /> : null}
                      </Stack>
                      <Box role='cell' sx={{ minWidth: 0 }}>
                        <EllipsisText text={joinOrEmpty((device.groups || []).map((group) => group.name))} testId={'machine-groups-' + device.id} />
                      </Box>
                      <Stack role='cell' direction='row' alignItems='center' spacing={1} sx={{ minWidth: 0 }}>
                        <Box component='span' data-testid={'machine-status-' + device.id}>
                          {/* E-1/RN54: UNKNOWN no es un estado de planta: se pinta "—", nunca IDLE. */}
                          {known ? <SystemValueChip look='overline' domain='DeviceStatusEnum' value={device.deviceStatus} tone={DEVICE_STATUS_TONE[device.deviceStatus] || 'default'} /> : EMPTY_VALUE}
                        </Box>
                        {device.active === true && phaseKey(device.jobStatusCode) !== ''
                          ? <Pill label={t_(phaseKey(device.jobStatusCode))} tone='success' testId={'machine-phase-' + device.id} />
                          : null}
                      </Stack>
                      <Box role='cell' sx={{ minWidth: 0 }}>
                        <EllipsisText text={joinOrEmpty((device.workers || []).map(workerFullName))} testId={'machine-workers-' + device.id} />
                      </Box>
                      <Typography role='cell' sx={{ textAlign: 'right' }} data-testid={'machine-since-' + device.id}>{since || EMPTY_VALUE}</Typography>
                    </Box>
                  )
                })}
              </Box>
              )}
        </Box>
        {activeDeviceCount(devices) === 0
          ? <Typography sx={{ px: 2, py: 1.5, borderTop: 1, borderColor: 'divider' }} color='text.secondary' data-testid='machines-no-active-execution'>{t_('No active execution')}</Typography>
          : null}
      </DetailSection>

      <DetailSection title={t_('Summary of the other blocks')} testId='general-summary'>
        <Stack direction='row' spacing={2} sx={{ p: 2 }}>
          {isVisible(JOB_DETAIL_TAB.MATERIALS)
            ? (
              <SummaryCard
                testId='summary-materials'
                title={t_('Materials')}
                value={t_('{{complete}} of {{total}}', { complete: materials.complete || 0, total: materials.total || 0 })}
                valueColor={pendingMaterials > 0 ? 'yellow.main' : partComponents > 0 ? 'text.primary' : 'primary.main'}
                detail={materialsDetail(pendingMaterials, partComponents, t_)}
                onClick={() => onOpenTab(JOB_DETAIL_TAB.MATERIALS)}
              />
              )
            : null}
          {isVisible(JOB_DETAIL_TAB.INSPECTIONS)
            ? (
              <SummaryCard
                testId='summary-inspections'
                title={t_('Inspections')}
                value={coveragePercent(inspections) + ' %'}
                valueColor='primary.main'
                // E-11 (RN62): solo informativas ⇒ "0 %" sin segunda línea.
                detail={(inspections.reportableFeatures || 0) > 0 ? t_('{{count}} out of tolerance', { count: inspections.outOfTolerance || 0 }) : ''}
                onClick={() => onOpenTab(JOB_DETAIL_TAB.INSPECTIONS)}
              />
              )
            : null}
          {isVisible(JOB_DETAIL_TAB.INCIDENCES)
            ? (
              <SummaryCard
                testId='summary-incidences'
                title={t_('Incidences')}
                value={t_('{{count}} open', { count: incidences.open || 0 })}
                valueColor='error.light'
                detail={t_('{{count}} pending actions', { count: incidences.pendingActions || 0 })}
                onClick={() => onOpenTab(JOB_DETAIL_TAB.INCIDENCES)}
              />
              )
            : null}
          <SummaryCard
            testId='summary-parts'
            title={t_('Associated parts')}
            value={(quantities.finished || 0) + ' / ' + (quantities.orderQuantity || 0)}
            detail={t_('{{count}} non-conforming', { count: quantities.nonConforming || 0 })}
            onClick={() => onOpenTab(JOB_DETAIL_TAB.PARTS)}
          />
        </Stack>
      </DetailSection>
    </Stack>
  )
}

GeneralTab.propTypes = {
  detail: PropTypes.object,
  visibleTabs: PropTypes.arrayOf(PropTypes.string),
  onOpenTab: PropTypes.func
}

export default React.memo(GeneralTab)
